import React, { PropsWithChildren, useEffect, useRef, useState } from "react";

export interface CollapseProps {
  id?: string;
  isOpen: boolean;
}

/**
 * Collapsible area that animates open and closed.
 * @param isOpen true if the area should be expanded.
 * @returns Collapsible control wrapped around child components
 */
export const Collapse = ({ id, children, isOpen }: PropsWithChildren<CollapseProps>) => {
  const [transitionComplete, setTransitionComplete] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    if (container && content) {
      const containerHeight = container.clientHeight;
      const contentHeight = content.clientHeight;

      if (isOpen) {
        container.style.height = `${contentHeight}px`;
      } else {
        container.style.overflow = "hidden";
        container.style.height = `${containerHeight}px`;
        setTimeout(() => (container.style.height = "0px"), 50);
      }

      if (isOpen) {
        if (timerRef?.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => setTransitionComplete(true), 200);
      }
    }
    return () => {
      if (timerRef?.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (transitionComplete && isOpen) {
      const container = containerRef.current;
      if (container) {
        container.style.overflow = "initial";
        container.style.height = "auto";
      }

      setTransitionComplete(false);
    }
  }, [transitionComplete, isOpen]);

  return (
    <div
      className="h-0 overflow-hidden transition-all duration-200"
      ref={containerRef}
      id={id}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};
